import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { addRpcUrlOverrideToChain, useFundWallet, useLinkAccount, usePrivy } from '@privy-io/react-auth';
import { ronin, saigon } from 'viem/chains';
import * as Sentry from '@sentry/react';

import { updateUserTelegram } from '../services/social.service';
import useChest from '../hook/useChest';
import useDailyCheckIn from '../hook/useDailyCheckin';
import useFish from '../hook/useFish';
import useCustomLogin from '../hook/useLogin';
import useCustomLogout from '../hook/useLogout';
import usePurchase from '../hook/usePurchase';
import useRod from '../hook/useRod';
import useTwitter from '../hook/useTwitter';
import useDiscord from '../hook/useDiscord';
import useWithdraw from '../hook/useWithdraw';
import useNFTChanges from '../hook/useNFTChanges';
import useLinkWallet from '../hook/useLinkWallet';
import useToken from '../hook/useToken';
import gameImage from '../bg-game.png';
import { disableNotifications, enableGameNotifications } from '../serviceWorkerRegistration';
import { isTelegram, os } from '../utils/browser';
import environments from '../utils/environments';
import useSpinWheel from '../hook/useSpinWheel';
import useWalletReady from '../hook/useWalletReady';
import useAvailabilityStore from '../stores/availability.store';
import useUserStore from '../stores/user.store';
import useGameStore from '../stores/game.store';
import { monitoring } from '../utils/monitoring';

const {
  PUBLIC_URL,
  PUBLIC_RPC_URL,
  GAME_VERSION,
  FISH_BE_ENDPOINT,
  FISH_WEBSOCKET_ENDPOINT,
  TOKEN_DASHBOARD_URL,
  TOKEN_DASHBOARD_BE_URL,
} = environments;

let baseCustomRPC;

const isProductionEVN = process.env.REACT_APP_ENVIRONMENT === 'prod';
const isShowBtn = process.env.REACT_APP_SHOW_DEV_MODE_BTN === 'true';

if (isProductionEVN) {
  baseCustomRPC = addRpcUrlOverrideToChain(ronin, PUBLIC_RPC_URL);
} else {
  baseCustomRPC = addRpcUrlOverrideToChain(saigon, PUBLIC_RPC_URL);
}

const telegramBot = process.env.REACT_APP_TELEGRAM_BOT_NAME;
const telegramApp = process.env.REACT_APP_TELEGRAM_APP_NAME;
const videoURL = process.env.REACT_APP_INTRO_VIDEO_URL;

const GameComponent = () => {
  const [tokenIds, setTokenIds] = useState([]); // State for list of tokenIds
  const [showVideo, setShowVideo] = useState(true);
  const [showPlayBtn, setShowPlayBtn] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isGameLoaded, setIsGameLoaded] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [listenerRegistered, setListenerRegistered] = useState(false);
  const [iframeReloadCount, setIframeReloadCount] = useState(0);
  const { ready: privyReady, user: privyUser, unlinkTwitter, unlinkDiscord, unlinkTelegram, exportWallet } = usePrivy();
  const walletReady = useWalletReady();
  const { postTweet, updateTwitterInfo } = useTwitter();
  const { updateDiscordInfo } = useDiscord();
  const { login } = useCustomLogin();
  const { customLogout } = useCustomLogout();
  const { purchaseGold, purchasePack, purchaseDivingTicket, purchaseLunarBait, purchaseCrownBait } = usePurchase();
  const { handleWithdraw } = useWithdraw();
  const { stakeFishToken, swapUsdcToFish, getSwapPreview, getUSDCBalance } = useToken();
  const { openLuckyChest, withdrawChest, openBatchNFTChest } = useChest();
  const { depositRod, withdrawRod, repairRod } = useRod();
  const { dailyCheckIn } = useDailyCheckIn();
  const { mintFish, withdrawFish, depositFish, cooking, addToCollection } = useFish();
  const { spinTokenWheel } = useSpinWheel();
  const user = useUserStore((state) => state.user);
  const setOpenModal = useAvailabilityStore((state) => state.setOpenModal);
  const setMessage = useAvailabilityStore((state) => state.setMessage);
  const setOnCloseModalCallback = useAvailabilityStore((state) => state.setOnCloseModalCallback);
  const setGameLoaded = useGameStore((state) => state.setGameLoaded);

  // Not sending message when successfully link account because game reloads the page anyway
  const { linkTwitter, linkDiscord, linkTelegram } = useLinkAccount({
    onSuccess: async (linkedAccount) => {
      // Handle Twitter link
      switch (linkedAccount.linkMethod) {
        case 'twitter':
          try {
            if (linkedAccount?.twitter) {
              await updateTwitterInfo(linkedAccount.twitter);
            } else if (linkedAccount?.user?.twitter) {
              const twitter = linkedAccount.user.twitter;
              twitter.avatar = twitter.profilePictureUrl;
              await updateTwitterInfo(twitter);
            }
          } catch (error) {
            console.error('Link Twitter failed:', error);
          }
          break;
        case 'discord':
          // Handle Discord link
          try {
            const discord = linkedAccount?.linkedAccount || linkedAccount?.user?.discord;
            const discordData = {
              subject: discord.subject,
            };
            await updateDiscordInfo(discordData);
          } catch (error) {
            console.error('Link Discord failed:', error);
          }
          break;
        case 'telegram':
          try {
            await updateTelegram(true);
          } catch (error) {
            console.error('Link Telegram failed:', error);
          }

          break;
      }
    },
  });
  const { getWalletAvailability, linkWallet } = useLinkWallet();

  const { fundWallet } = useFundWallet({
    onUserExited() {
      sendMsgToGameClient({
        type: 'depositWalletResult',
        // message: 'Deposit Successful',
        // isSuccess: true,
        hasWaiting: true,
      });
      activeWaiting(false);
    },
  });

  const handleTokenIdsChange = (event) => {
    const input = event.target.value;
    // Split the input by commas, trim whitespace, and parse as integers
    const ids = input
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    setTokenIds(ids);
  };

  const toggleDevMode = () => {
    setDevMode((prevDevMode) => !prevDevMode);
  };

  // latest social states for unlinking
  const twitterSubject = useRef(privyUser?.twitter?.subject || null);
  const discordSubject = useRef(privyUser?.discord?.subject || null);
  const telegramUserId = useRef(privyUser?.telegram?.telegramUserId || null);

  useEffect(() => {
    twitterSubject.current = privyUser?.twitter?.subject || null;
  }, [privyUser?.twitter?.subject]);

  useEffect(() => {
    discordSubject.current = privyUser?.discord?.subject || null;
  }, [privyUser?.discord?.subject]);

  useEffect(() => {
    telegramUserId.current = privyUser?.telegram?.telegramUserId || null;
  }, [privyUser?.telegram?.telegramUserId]);

  const [containerStyle, setContainerStyle] = useState({
    height: '100dvh',
    width: '100vw',
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  });

  const iframeRef = useRef(null);
  const handlersRef = useRef({});

  // event handlers that need latest state
  const onInGameLinkTelegramBtnClicked = async () => {
    try {
      if (telegramUserId.current) {
        await unlinkTelegram(telegramUserId.current);
      }
      linkTelegram();
    } catch (error) {
      console.error('Failed to link Telegram:', error);
      await updateTelegram(true); // try to update anyway
    }
  };
  const onInGameOpenLuckyChestBtnClicked = async (tokenId) => {
    const { success, message, data } = await openLuckyChest(tokenId, () => {
      sendMsgToGameClient({ type: 'getConfirmOpenNFTChest', message: 'Confirm Successful' });
    });
    sendMsgToGameClient({ type: 'openNFTChestResult', message, success, hasWaiting: true, data });
  };

  const onInGameSpinTokenWheelClicked = async () => {
    activeWaiting(true);
    const { success, message, data } = await spinTokenWheel();
    activeWaiting(false);
    sendMsgToGameClient({ type: 'spinTokenWheelResult', message, success, hasWaiting: true, data });
  };

  // Store handlers that depend on frequently-changing state
  // to ensure they always have the latest values when called from message events
  handlersRef.current = {
    onInGameLinkTelegramBtnClicked,
    onInGameOpenLuckyChestBtnClicked, // Needs latest luckyChestConfig from openLuckyChest
    onInGameSpinTokenWheelClicked,
    // Add more here if they start using stateful hooks
  };

  useEffect(() => {
    return () => {
      localStorage.removeItem('reauth_done');
    };
  }, []);

  // check privy timeout
  useEffect(() => {
    // Set a timeout to register listeners even if Privy never initializes
    const timeoutId = setTimeout(() => {
      if (!listenerRegistered) {
        const message = 'Wallet initialization timed out. Log out and login again to refresh your session.';
        setMessage(message);
        setOpenModal(true);
        setOnCloseModalCallback(customLogout);
      }
    }, 10_000); // 10 seconds timeout

    return () => clearTimeout(timeoutId);
  }, [privyReady, walletReady, listenerRegistered]);

  // refresh user data on user changed (eg from guest to privy user)
  const prevUserIdRef = useRef(null);
  useEffect(() => {
    const userChanged = user && prevUserIdRef.current !== null && prevUserIdRef.current !== user.userPrivyId;
    if (userChanged) setIframeReloadCount((c) => c + 1); // force iframe remount

    // mark this so next call userChanged will be true & trigger refresh
    if (user) prevUserIdRef.current = user.userPrivyId;
  }, [user]);

  useEffect(() => {
    const isGuest = localStorage.getItem('isGuest') === 'true';

    if (!isGuest && (!walletReady || !privyReady)) {
      console.log('--------Privy wallets not ready');
      return;
    }

    // register listeners
    console.log('--------Privy wallets ready, registering event listeners');

    const handleMessage = (event) => {
      if (event.source === iframeRef.current?.contentWindow) {
        try {
          if (event.data.type === 'registerCompleted') {
            console.log('User skipped or finished tutorial');
            fbq('track', 'CompleteRegistration');
            rdt('track', 'CompleteRegistration');
          } else if (event.data.type === 'buttonLogInClicked') {
            onInGameLoginBtnClicked();
          } else if (event.data.type === 'buttonLogOutClicked') {
            onInGameLogOutBtnClicked();
          } else if (event.data.type === 'buttonBuyCoinClicked') {
            onInGameBuyGoldBtnClicked(event.data.data);
          } else if (event.data.type === 'buttonPurchaseStarterPackClicked') {
            onInGameBuyPackageBtnClicked(
              event.data.amountETH,
              event.data.packId,
              event.data.packName,
              event.data.paymentToken
            );
          } else if (event.data.type === 'buttonBuyDivingTicketClicked') {
            onInGameBuyDivingTicketBtnClicked(event.data.quantity, event.data.paymentToken);
          } else if (event.data.type === 'buttonBuyLunarBaitClicked') {
            onInGameBuyLunarBaitBtnClicked(event.data.quantity);
          } else if (event.data.type === 'buttonBuyCrownBaitClicked') {
            onInGameBuyCrownBaitBtnClicked(event.data.quantity);
          } else if (event.data.type === 'buttonDepositClicked') {
            cocosDepositBtnClick(event.data.message);
          } else if (event.data.type === 'buttonExportWalletClicked') {
            cocosExportWalletBtnClick();
          } else if (event.data.type === 'buttonWithdrawClicked') {
            cocosWithdrawBtnClick(event.data.address, event.data.amountETH);
          } else if (event.data.type === 'buttonLinkTwitterClicked') {
            cocosLinkTwitterBtnClick(event.data.data);
          } else if (event.data.type === 'buttonUnlinkTwitterClicked') {
            cocosUnlinkTwitterBtnClick(event.data.data);
          } else if (event.data.type === 'buttonLinkDiscordClicked') {
            cocosLinkDiscordBtnClick(event.data.data);
          } else if (event.data.type === 'buttonUnlinkDiscordClicked') {
            cocosUnlinkDiscordBtnClick(event.data.data);
          } else if (event.data.type === 'buttonLinkTelegramClicked') {
            handlersRef.current.onInGameLinkTelegramBtnClicked();
          } else if (event.data.type === 'buttonUnlinkTelegramClicked') {
            onInGameUnlinkTelegramBtnClicked();
          } else if (event.data.type === 'buttonShareFishClicked') {
            cocosShareFishBtnClick(event.data.data);
          } else if (event.data.type === 'buttonShareOnTelegram') {
            cocosShareTelegramBtnClick(event.data.data);
          } else if (event.data.type === 'OpenTelegramInvoice') {
            cocosOpenTelegramInvoice(event.data.data);
          } else if (event.data.type === 'OpenHttpLink') {
            cocosOpenLink(event.data.data);
          } else if (event.data.type === 'buttonStakeFISHClicked') {
            onInGameStakeBtnClicked(event.data.amount, event.data.durationInSeconds);
          } else if (event.data.type === 'swapInputChanged') {
            onInGameSwapInputChanged(event.data.inputToken, event.data.amount);
          } else if (event.data.type === 'requestUSDCBalance') {
            updateUSDCBalance();
          } else if (event.data.type === 'buttonSwapClicked') {
            onInGameSwapBtnClicked(event.data.amount);
          } else if (event.data.type === 'buttonSpinDailyTokenWheelClicked') {
            handlersRef.current.onInGameSpinTokenWheelClicked();
          } else if (event.data.type === 'buttonOpenLuckyChestClicked') {
            handlersRef.current.onInGameOpenLuckyChestBtnClicked(event.data.tokenId);
          } else if (event.data.type === 'buttonWithdrawNFTChestClicked') {
            cocosWithdrawNFTChestBtnClick(event.data.chestInfos);
          } else if (event.data.type === 'buttonOpenBatchNFTChestClicked') {
            onInGameOpenBatchNFTChestCliked(event.data.chestInfos);
          } else if (event.data.type === 'buttonDepositNFTRodClicked') {
            cocosDepositRodBtnClick(event.data.tokenIds);
          } else if (event.data.type === 'buttonWithdrawNFTRodClicked') {
            cocosWithdrawRodBtnClick(event.data.tokenIds);
          } else if (event.data.type === 'buttonRepairNFTRodClicked') {
            cocosRepairRodBtnClick(event.data.tokenId, event.data.paymentToken);
          } else if (event.data.type === 'buttonDailyCheckInClicked') {
            cocosDailyCheckInBtnClick(event.data.questId);
          } else if (event.data.type === 'buttonMintNFTFishClicked') {
            cocosMintFishBtnClick(event.data.fishIds);
          } else if (event.data.type === 'buttonWithdrawNFTFishClicked') {
            cocosWithdrawFishBtnClick(event.data.fishIds, event.data.tokenIds);
          } else if (event.data.type === 'buttonDepositNFTFishClicked') {
            cocosDepositFishBtnClick(event.data.tokenIds);
          } else if (event.data.type === 'buttonBurnNFTFishClicked') {
            cocosCookingBtnClick(event.data.inGameTokenIds, event.data.inWalletTokenIds);
          } else if (event.data.type === 'buttonCollectNFTFishClicked') {
            onInGameCollectNFTFishBtnClicked(event.data.fish);
          } else if (event.data.type === 'buttonLinkWalletClicked') {
            onInGameLinkWalletBtnClicked();
          } else if (event.data.type === 'walletProviderSelected') {
            onWalletProviderSelected(event.data.providerKey);
          } else if (event.data.type === 'buttonTurnOffIntro') {
            turnOffGameIntro();
          } else if (event.data.type === 'buttonTurnOnOffNotification') {
            handleNotificationSettingsClick(event.data);
          } else if (event.data.type === 'actionNotificationBrowser') {
            changeNotificationPwa(event.data);
          } else if (event.data.type === 'loadingProgress') {
            cocosLoadingProgress(event.data);
          } else {
            sendAuthDataToGameClient();
          }
        } catch (error) {
          console.error('Error handling message:', error);
        }
      } else if (event.data.type === 'actionNotificationBrowser') {
        changeNotificationPwa(event.data);
      }
    };

    window.addEventListener('message', handleMessage);

    const iframe = iframeRef.current;
    if (iframe) {
      window.dispatchGameMessageEvent = (data) => iframeRef.current.contentWindow.postMessage(data, '*');
      setIsGameLoaded(true);
    }

    setListenerRegistered(true);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [privyReady, walletReady]);

  useNFTChanges(isGameLoaded && !showVideo);

  const activeWaiting = async (isWaiting) => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage({ type: 'activeWaiting', isWaiting: isWaiting }, '*');
    }
  };

  const changeNotificationPwa = async (data) => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(data, '*');
    }
  };

  const sendMsgToGameClient = async ({ type, message, success, hasWaiting, data, ...rest }) => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(
        {
          type,
          message,
          isSuccess: success,
          hasWaiting,
          data,
          ...rest, // custom data
        },
        '*'
      );
    }
  };

  const sendAuthDataToGameClient = async () => {
    const authToken = localStorage.getItem('fishAuth');
    const refreshToken = localStorage.getItem('refreshToken');
    const userId = localStorage.getItem('userId');

    const authData = {
      authToken,
      refreshToken,
      userId,
      beAddress: FISH_BE_ENDPOINT,
      websocketAddress: FISH_WEBSOCKET_ENDPOINT,
      isTelegram,
      isMobile,
      os,
      telegramBot,
      telegramApp,
      gameVersion: GAME_VERSION,
      isDevEnv: !isProductionEVN,
      gameUrl: PUBLIC_URL,
      tokenDashboardUrl: TOKEN_DASHBOARD_URL,
      tokenDashboardBeUrl: TOKEN_DASHBOARD_BE_URL,
      type: 'initAuth',
    };

    // 1️⃣ Send to the Cocos game iframe
    if (iframeRef?.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(authData, '*');
    }

    // 2️⃣ Send to parent (if in iframe)
    if (window.parent !== window) {
      window.parent.postMessage(authData, '*');
    }
  };

  const loginInProgressRef = useRef(false);
  const onInGameLoginBtnClicked = async () => {
    // debounce
    if (loginInProgressRef.current) {
      return;
    }

    // flush heavy listeners that might cause performance issue during login process, especially on iOS
    if (monitoring.intervalId) {
      clearInterval(monitoring.intervalId);
      monitoring.intervalId = null;
    }
    if (monitoring.observer) {
      monitoring.observer.disconnect();
      monitoring.observer = null;
    }

    loginInProgressRef.current = true;

    try {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Login button for guests clicked',
        level: 'info',
        data: {
          memoryUsedMB: performance.memory ? (performance.memory.usedJSHeapSize / 1048576).toFixed(2) : 'unavailable',
          iframeVisible: !showVideo,
        },
      });
      login();
    } finally {
      setTimeout(() => {
        loginInProgressRef.current = false;
      }, 5000);
    }
  };

  const onInGameLogOutBtnClicked = async () => {
    customLogout();
  };

  const onInGameBuyGoldBtnClicked = async (input) => {
    activeWaiting(true);
    const { success, message, data } = await purchaseGold(input);
    activeWaiting(false);
    // sendMsgToGameClient({ type: 'refreshUserData', message, success, hasWaiting: true });
    sendMsgToGameClient({ type: 'purchaseGoldResult', message, success, hasWaiting: true, data });
  };

  const onInGameBuyPackageBtnClicked = async (amountETH, packId, packName, token) => {
    activeWaiting(true);
    const { success, message, data } = await purchasePack(amountETH, packId, packName, token);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'purchasePackResult', message, success, hasWaiting: true, data });
  };

  const onInGameBuyDivingTicketBtnClicked = async (quantity, token) => {
    activeWaiting(true);
    const { success, message, data } = await purchaseDivingTicket(quantity, token);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'purchaseDivingTicketResult', message, success, hasWaiting: true, data });
  };

  const onInGameBuyLunarBaitBtnClicked = async (quantity) => {
    activeWaiting(true);
    const { success, message, data } = await purchaseLunarBait(quantity);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'purchaseLunarBaitResult', message, success, hasWaiting: true, data });
  };

  const onInGameBuyCrownBaitBtnClicked = async (quantity) => {
    activeWaiting(true);
    const { success, message, data } = await purchaseCrownBait(quantity);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'purchaseCrownBaitResult', message, success, hasWaiting: true, data });
  };

  const turnOffGameIntro = async () => {
    setShowVideo(false);
    setGameLoaded(true);
  };

  const cocosDepositBtnClick = async (amount) => {
    const embeddedWallet = privyUser.linkedAccounts.find((acc) => acc.connectorType === 'embedded');
    await fundWallet({
      address: embeddedWallet?.address || privyUser.wallet.address,
      options: {
        chain: baseCustomRPC,
        amount: amount.toString(),
        asset: 'native-currency',
        // TODO not use defaultFundingMethod & test card method
        defaultFundingMethod: 'manual',
      },
    });
  };

  const onInGameStakeBtnClicked = async (amount, duration) => {
    activeWaiting(true);
    const { success, message, data } = await stakeFishToken(amount, duration);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'stakeFISHResult', message, success, hasWaiting: true, data });
  };

  const onInGameSwapInputChanged = async (inputToken, amount) => {
    const { success, message, data } = await getSwapPreview({ inputToken, amount });
    sendMsgToGameClient({ type: 'swapQuoteData', message, success, hasWaiting: true, data });
  };

  const updateUSDCBalance = async () => {
    const usdcBalance = await getUSDCBalance();
    const data = { usdcBalance };
    sendMsgToGameClient({ type: 'updateUSDCBalance', success: true, hasWaiting: false, data });
  };

  const onInGameSwapBtnClicked = async (ronAmount) => {
    activeWaiting(true);
    const { success, message, data } = await swapUsdcToFish(ronAmount);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'swapResult', message, success, hasWaiting: true, data });

    updateUSDCBalance();
  };

  const cocosExportWalletBtnClick = () => {
    exportWallet();
  };

  const cocosLinkTwitterBtnClick = async (data) => {
    if (!twitterSubject.current) {
      linkTwitter();
    } else {
      try {
        await unlinkTwitter(twitterSubject.current);
        await updateTwitterInfo(null);
        linkTwitter();
      } catch (error) {
        console.error('Failed to unlink and re-link Twitter:', error);
      }
    }
  };

  const cocosUnlinkTwitterBtnClick = async (data) => {
    try {
      await unlinkTwitter(twitterSubject.current);
      const result = await updateTwitterInfo(null);
      sendMsgToGameClient({
        type: 'refreshUserUnlinkTwitter',
        message: 'Successfully unlink X',
        hasWaiting: true,
        isSuccess: true,
        data: result,
      });
    } catch (error) {
      console.error('Unlink Twitter failed:', error);
      sendMsgToGameClient({
        type: 'refreshUserUnlinkTwitter',
        message: error?.message || 'Failed to unlink X',
        hasWaiting: true,
        isSuccess: false,
      });
    }
  };

  const cocosLinkDiscordBtnClick = async () => {
    if (!discordSubject.current) {
      linkDiscord();
    } else {
      try {
        await unlinkDiscord(discordSubject.current);
        await updateDiscordInfo(null);
        linkDiscord();
      } catch (error) {
        console.error('Failed to unlink and re-link Discord:', error);
      }
    }
  };

  const cocosUnlinkDiscordBtnClick = async () => {
    try {
      await unlinkDiscord(discordSubject.current);
      const result = await updateDiscordInfo(null);
      sendMsgToGameClient({
        type: 'refreshUserUnlinkDiscord',
        message: 'Successfully unlink Discord',
        hasWaiting: true,
        isSuccess: true,
        data: result,
      });
    } catch (error) {
      console.error('Unlink Discord failed:', error);
      sendMsgToGameClient({
        type: 'refreshUserUnlinkDiscord',
        message: error?.message || 'Failed to unlink Discord',
        hasWaiting: true,
        isSuccess: false,
      });
    }
  };

  const updateTelegram = async (isLink = true) => {
    const action = isLink ? 'link' : 'unlink';
    const result = await updateUserTelegram();
    console.log(`${action} Telegram success:`, result.data);

    sendMsgToGameClient({
      type: 'refreshUserTelegram',
      message: `Successfully ${action}ed Telegram`,
      hasWaiting: true,
      isSuccess: true,
      data: result.data,
    });
  };

  const onInGameUnlinkTelegramBtnClicked = async () => {
    try {
      try {
        await unlinkTelegram(telegramUserId.current);
      } catch (error) {
        console.error('Unlink Telegram Privy failed:', error);
      }
      await updateTelegram(false);
    } catch (error) {
      console.error('Unlink Telegram failed:', error);
      sendMsgToGameClient({
        type: 'refreshUserTelegram',
        message: error?.message || 'Failed to unlink Telegram',
        hasWaiting: true,
        isSuccess: false,
      });
    }
  };

  const cocosWithdrawBtnClick = async (address, amount) => {
    activeWaiting(true);
    const { success, message, data } = await handleWithdraw(address.trim(), amount.trim());
    sendMsgToGameClient({
      type: 'withdrawWalletResult',
      message,
      data,
      isSuccess: success,
      hasWaiting: true,
    });
    activeWaiting(false);
  };

  const cocosShareFishBtnClick = async (data) => {
    await postTweet(data);
  };

  const cocosShareTelegramBtnClick = async (refCode) => {
    const message = 'FISHING FRENZY' + '\n';
    const link = 'https://t.me/share/url?url=https://t.me/' + telegramBot + '/' + telegramApp + '?startapp=' + refCode; // + "&text=" + encodeURIComponent(message);

    // window.Telegram.WebApp.openLink(link);
    window.Telegram.WebApp.openTelegramLink(link);
  };

  const cocosOpenLink = async (link) => {
    try {
      if (isTelegram) window.Telegram.WebApp.openLink(link);
      else window.open(link);
    } catch (error) {
      console.error('Open link error:', error);
    }
    activeWaiting(false);
  };

  const cocosOpenTelegramInvoice = async (invoiceLink) => {
    window.Telegram.WebApp.openLink(invoiceLink);
  };

  const cocosWithdrawNFTChestBtnClick = async (chestInfos) => {
    const { success, message, data } = await withdrawChest(chestInfos);
    sendMsgToGameClient({ type: 'withdrawChestResult', message, success, hasWaiting: true, data });
  };

  const onInGameOpenBatchNFTChestCliked = async (chestInfos) => {
    const { success, message, data } = await openBatchNFTChest(chestInfos, () =>
      sendMsgToGameClient({ type: 'getConfirmOpenNFTChest', message: 'Confirm Successful' })
    );
    sendMsgToGameClient({ type: 'openNFTChestResult', message, success, hasWaiting: true, data });
  };

  const cocosDepositRodBtnClick = async (tokenIds) => {
    activeWaiting(true);
    const { success, message, data } = await depositRod(tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTRodResult', message, success, hasWaiting: true, data });
  };

  const cocosWithdrawRodBtnClick = async (tokenIds) => {
    activeWaiting(true);
    const { success, message, data } = await withdrawRod(tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTRodResult', message, success, hasWaiting: true, data });
  };

  const cocosRepairRodBtnClick = async (tokenIds, paymentToken) => {
    activeWaiting(true);
    const { success, message, data } = await repairRod(tokenIds, paymentToken);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTRodResult', message, success, hasWaiting: true, data });
    sendMsgToGameClient({ type: 'getRepairRodResult', message, success, hasWaiting: true, data });
  };

  const cocosDailyCheckInBtnClick = async (questId) => {
    activeWaiting(true);
    const { success, message, data } = await dailyCheckIn();
    activeWaiting(false);
    sendMsgToGameClient({ type: 'dailyCheckInResult', message, success, hasWaiting: true, data, questId });
  };

  const cocosWithdrawFishBtnClick = async (fishIds, tokenIds) => {
    activeWaiting(true);
    const { success, message, data } = await withdrawFish(fishIds, tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTFishResult', message, success, hasWaiting: true, data });
  };

  const cocosMintFishBtnClick = async (fishIds) => {
    activeWaiting(true);
    const { success, message, data } = await mintFish(fishIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTFishResult', message, success, hasWaiting: true, data });
  };

  const cocosDepositFishBtnClick = async (tokenIds) => {
    activeWaiting(true);
    const { success, message, data } = await depositFish(tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTFishResult', message, success, hasWaiting: true, data });
  };

  const cocosCookingBtnClick = async (stakedTokenIds, tokenIds) => {
    activeWaiting(true);
    const { success, message, transactionHash } = await cooking(stakedTokenIds, tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'burnNFTFishResult', message, success, hasWaiting: true, transactionHash });
  };

  const onInGameCollectNFTFishBtnClicked = async (fish) => {
    activeWaiting(true);
    const { success, message, data } = await addToCollection(fish);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'collectNFTFishResult', message, success, hasWaiting: true, data });
  };

  const onInGameLinkWalletBtnClicked = async () => {
    const data = getWalletAvailability();
    sendMsgToGameClient({ type: 'walletProvidersAvailability', data });
  };

  const onWalletProviderSelected = async (providerKey) => {
    activeWaiting(true);
    const { success, message, data } = await linkWallet(providerKey);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'linkWalletResult', message, success, hasWaiting: true, data });
  };

  const cocosLoadingProgress = async ({ progress, status }) => {
    if (progress === 100) setShowPlayBtn(true);
    setProgress(progress);
    setStatusText(status);
  };

  // manually detect video end
  const handleTimeUpdate = (e) => {
    if (e.target.currentTime >= e.target.duration - 0.5) {
      onIntroVideoFinish();
    }
  };

  const onIntroVideoFinish = async () => {
    if (progress === 100) turnOffGameIntro();
  };

  const handleNotificationSettingsClick = async (data) => {
    if (data.isTurnOn) {
      enableGameNotifications();
    } else {
      disableNotifications();
    }
  };

  return (
    <div style={containerStyle}>
      {showVideo && !isVideoLoaded && <img src={gameImage} alt="game" className="intro-image" />}
      {showVideo && (
        <div className="intro-container">
          <video
            className="intro-video"
            autoPlay
            muted
            loop
            playsInline
            onCanPlay={() => setIsVideoLoaded(true)}
            onTimeUpdate={handleTimeUpdate}
            onError={() => setIsVideoLoaded(false)}
            controls={false}
            preload="auto">
            <source src={videoURL} type="video/mp4" />
          </video>
          <div className="loading-waiting-container">
            <span className="loading-resource-text">{statusText}</span>
            <div className="loading-waiting-text">
              <div className="progress-bar-container">
                <div
                  className={`progress-bar-text ${
                    showPlayBtn ? 'progress-bar-text-complete' : 'progress-bar-text-loading'
                  }`}></div>
                <div
                  className="progress-bar"
                  style={{
                    width: `${progress}%`,
                    marginLeft: progress === 100 ? '0' : undefined,
                  }}></div>
              </div>
              {showPlayBtn ? (
                <button className="Play-button" onClick={turnOffGameIntro}>
                  <span className="press-start-2p-regular">Play Now</span>
                </button>
              ) : (
                <div className="loading-waiting-message">
                  <div>First load may take longer</div>
                  <div>Thank you for your patience</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <iframe
        key={iframeReloadCount} // update this to force game rerender
        ref={iframeRef}
        src="/cocos/index.html"
        title="Cocos Game"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: showVideo ? -1 : 1,
          visibility: showVideo ? 'hidden' : 'visible',
        }}
      />

      {isShowBtn && (
        <>
          <button onClick={toggleDevMode} className="First-button">
            {devMode ? 'Exit Dev Mode' : 'Dev Mode'}
          </button>

          {devMode && (
            <>
              <input
                className="Second-button"
                type="text"
                placeholder="Test Enter token IDs, comma-separated"
                onChange={handleTokenIdsChange}
              />
              {/* <button className="Third-button" onClick={() => cocosMintFishBtnClick(['67eb666a2f3ef82ec2f25a53'])}>
                Test: Mint Fish
              </button> */}
              {/* <button className="Third-button" onClick={() => onInGameCollectNFTFishBtnClicked([4])}>
                Test: Add Collection
              </button> */}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default GameComponent;
